import { useState } from 'react';

import type { PreviewTarget } from '@/features/proactive-agents/proactive-agents-types';
import { useDebouncedValue } from '@/features/proactivity-experiments/useDebouncedValue';

export type PickerMode = 'everyone' | 'emails' | 'contains';

const buildTarget = (
  agent: string,
  mode: PickerMode,
  emails: string[],
  emailContains: string
): PreviewTarget | null => {
  if (!agent) return null;
  if (mode === 'emails')
    return emails.length ? { agent, emails, email_contains: null } : null;
  if (mode === 'contains')
    return emailContains.trim()
      ? { agent, emails: [], email_contains: emailContains.trim() }
      : null;
  return { agent, emails: [], email_contains: null };
};

// The email fields of a section, with the typed text debounced before it reaches the preview's target. Passing
// `everyoneLabel` adds an "everyone" mode, and the picker starts in it.
export const useEmailPicker = (
  agent: string,
  { everyoneLabel }: { everyoneLabel?: string } = {}
) => {
  const [mode, setMode] = useState<PickerMode>(
    everyoneLabel ? 'everyone' : 'emails'
  );
  const [emails, setEmails] = useState<string[]>([]);
  const [emailContains, setEmailContains] = useState('');
  const debouncedContains = useDebouncedValue(emailContains, 300);
  return {
    target: buildTarget(agent, mode, emails, debouncedContains),
    // False while typed text has not reached the target yet
    inputIsSettled: mode !== 'contains' || debouncedContains === emailContains,
    everyoneLabel,
    mode,
    setMode,
    emails,
    setEmails,
    emailContains,
    setEmailContains,
  };
};

export type EmailPicker = ReturnType<typeof useEmailPicker>;
